'use client';

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import {
  resolveAuroraLabels,
  type AuroraLabels,
} from '@/components/aurora/labels';

interface SolarWindMetric {
  value: number | null;
  timestamp: string | null;
}

type RtswRow = Record<string, unknown>;

// NOAA's real-time solar wind (RTSW) feeds are arrays of per-minute objects; the most
// recent rows can carry null gaps, so walk back to the last row with a numeric value.
function lastValid(rows: RtswRow[], field: string): SolarWindMetric {
  for (let i = rows.length - 1; i >= 0; i--) {
    const raw = rows[i]?.[field];
    if (raw === null || raw === undefined) continue;
    const val = typeof raw === 'number' ? raw : parseFloat(String(raw));
    if (!isNaN(val)) {
      return { value: val, timestamp: (rows[i]?.time_tag as string) ?? null };
    }
  }
  return { value: null, timestamp: null };
}

function MetricCard({
  title,
  value,
  unit,
  color,
  note,
}: {
  title: string;
  value: number | null;
  unit: string;
  color: string;
  note: string;
}) {
  return (
    <div
      className={cn(
        'flex-1 rounded-xl p-5 transition-colors duration-300',
        'bg-white shadow-sm',
      )}
    >
      <p
        className={cn(
          'text-xs font-sans uppercase tracking-wider mb-3',
          'text-[#5A5A6E]',
        )}
      >
        {title}
      </p>
      <div className="flex items-baseline gap-1.5">
        <span
          className="text-4xl font-sans font-semibold"
          style={{ color }}
        >
          {value !== null ? value.toFixed(1) : '--'}
        </span>
        <span
          className={cn(
            'text-sm',
            'text-[#5A5A6E]',
          )}
        >
          {unit}
        </span>
      </div>
      <p
        className={cn(
          'text-xs mt-2',
          'text-[#5A5A6E]',
        )}
      >
        {note}
      </p>
    </div>
  );
}

function getBzColor(bz: number | null): string {
  if (bz === null) return '#8A8A9A';
  if (bz < -10) return '#EF4444';
  if (bz < 0) return '#F97316';
  return '#22C55E';
}

function getSpeedColor(speed: number | null): string {
  if (speed === null) return '#8A8A9A';
  if (speed > 700) return '#EF4444';
  if (speed > 500) return '#F97316';
  return '#22C55E';
}

function getDensityColor(density: number | null): string {
  if (density === null) return '#8A8A9A';
  if (density > 20) return '#EF4444';
  if (density > 10) return '#F97316';
  return '#22C55E';
}

export default function SolarWindPanel({ labels }: { labels?: AuroraLabels }) {
  const L = resolveAuroraLabels(labels);
  const [bz, setBz] = useState<SolarWindMetric>({ value: null, timestamp: null });
  const [speed, setSpeed] = useState<SolarWindMetric>({ value: null, timestamp: null });
  const [density, setDensity] = useState<SolarWindMetric>({ value: null, timestamp: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);

    Promise.all([
      fetch('https://services.swpc.noaa.gov/json/rtsw/rtsw_mag_1m.json').then((r) => {
        if (!r.ok) throw new Error('mag fetch failed');
        return r.json();
      }),
      fetch('https://services.swpc.noaa.gov/json/rtsw/rtsw_wind_1m.json').then((r) => {
        if (!r.ok) throw new Error('wind fetch failed');
        return r.json();
      }),
    ])
      .then(([magData, windData]: [RtswRow[], RtswRow[]]) => {
        setBz(lastValid(magData, 'bz_gsm'));
        setSpeed(lastValid(windData, 'proton_speed'));
        setDensity(lastValid(windData, 'proton_density'));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      <p
        className={cn(
          'text-xs font-sans uppercase tracking-wider',
          'text-[#5A5A6E]',
        )}
      >
        {L.solarTitle}
      </p>

      {loading && (
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={cn(
                'h-32 rounded-xl animate-pulse',
                'bg-white shadow-sm',
              )}
            />
          ))}
        </div>
      )}

      {error && (
        <p
          className={cn(
            'text-sm text-center py-4',
            'text-[#5A5A6E]',
          )}
        >
          {L.solarUnavailable}
        </p>
      )}

      {!loading && !error && (
        <div className="grid gap-4 md:grid-cols-3">
          <MetricCard
            title={L.bzTitle}
            value={bz.value}
            unit="nT"
            color={getBzColor(bz.value)}
            note={L.bzNote}
          />
          <MetricCard
            title={L.speedTitle}
            value={speed.value}
            unit="km/s"
            color={getSpeedColor(speed.value)}
            note={L.speedNote}
          />
          <MetricCard
            title={L.densityTitle}
            value={density.value}
            unit="p/cm³"
            color={getDensityColor(density.value)}
            note={L.densityNote}
          />
        </div>
      )}
    </div>
  );
}
